import CustomDialog from 'common/components/CustomDialog'
import useYupValidationResolver from 'common/hooks/useYupValidationResolver'
import { errorMessageByField } from 'common/utils/hook-form'
import { numberSchema } from 'common/utils/yup'
import { useFarmGroup } from 'modules/member/api/selector'
import { useMutateCreatePond, useMutateEditPond } from 'modules/pond/api/mutation'
import { useGetPondAreaUnits, useGetPondTypes, useGetSpecies } from 'modules/pond/api/queries'
import { selectedPondState } from 'modules/pond/stores/atom'
import { useState, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'
import * as Yup from 'yup'

import Typography from '@mui/material/Typography'

import MenuItem from '@mui/material/MenuItem'
import TextField from '@mui/material/TextField'
import Stack from '@mui/system/Stack'
import { Box, FormHelperText, InputAdornment, InputBase, Select, useTheme } from '@mui/material'
import { Paper } from '@mui/material'
import UnsavedChangesDialog from 'common/components/UnsavedChangesDialog'

interface PondDialogProps {
  open: boolean
  onClose: () => void
  isEditMode?: boolean
}

type FormDataType = {
  name: string
  pondType: number
  pondSize: string
  species: number
  areaUnit: string
}

const PondDialog = ({ open, onClose, isEditMode = false }: PondDialogProps) => {
  const { t } = useTranslation(['common', 'pond'])
  const theme = useTheme()
  const { refetch: refetchFarmGroup } = useFarmGroup('', { enabled: false })

  const pondInfo = useRecoilValue(selectedPondState)
  const [isLoading, setIsLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const { data: pondTypes, isLoading: loadingType } = useGetPondTypes()
  const { data: species, isLoading: loadingSpecies } = useGetSpecies()
  const { data: pondArea } = useGetPondAreaUnits()

  const createPondMutation = useMutateCreatePond()
  const editPondMutation = useMutateEditPond()

  const schema = Yup.object().shape({
    name: Yup.string()
      .required(t('pond:please.input.pond.name'))
      .max(30, t('pond:pond.name.max.length')),
    pondType: Yup.number().positive(t('pond:please.input.pond.type')),
    pondSize: numberSchema(t('pond:require.number')).required(t('pond:please.input.pond.size')),
    species: Yup.number().positive(t('pond:please.input.species')),
    areaUnit: Yup.string().required(t('pond:please.input.pond.area')),
  })

  const resolver = useYupValidationResolver(schema)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormDataType>({
    defaultValues: {
      name: '',
      pondType: -1,
      pondSize: '',
      species: -1,
      areaUnit: '',
    },
    resolver,
  })

  useEffect(() => {
    if (isEditMode && pondInfo) {
      reset({
        name: pondInfo.name,
        pondType: pondInfo.pondType.id,
        pondSize: `${pondInfo.pondSize}`,
        species: pondInfo.species.id,
        areaUnit: pondInfo.areaUnit,
      })
    } else if (pondArea && pondArea.length > 0) {
      reset((prev) => ({
        ...prev,
        areaUnit: pondArea[0].name,
      }))
    }
  }, [isEditMode, pondInfo, pondArea, reset])

  const onSubmit = async (data: FormDataType) => {
    setIsLoading(true)

    if (data.pondType === -1) {
      setIsLoading(false)
      return
    }

    const body = {
      name: data.name,
      pondTypeId: data.pondType,
      pondSize: Number(data.pondSize),
      speciesId: data.species,
      areaUnit: data.areaUnit,
    }
    if (isEditMode && pondInfo) {
      await editPondMutation
        .mutateAsync({
          pondId: pondInfo.id,
          body: body,
        })
        .catch((error) => {
          const rawMessage = error?.response?.data?.message || ''
          const code = rawMessage.split(':').pop()?.trim()
          if (code === 'FARM_NAME_ALREADY_EXIST') {
            setError('name', {
              type: 'server',
              message: t('pond:duplicate.pond.name'),
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
          onClose()
          reset()
        })
    } else {
      await createPondMutation
        .mutateAsync({
          body: body,
        })
        .catch((error) => {
          const rawMessage = error?.response?.data?.message || ''
          const code = rawMessage.split(':').pop()?.trim()
          if (code === 'FARM_NAME_ALREADY_EXIST') {
            setError('name', {
              type: 'server',
              message: t('pond:duplicate.pond.name'),
            })
          }
        })
        .finally(() => {
          setIsLoading(false)
          onClose()
          reset()
          refetchFarmGroup()
        })
    }
  }

  const title = isEditMode ? t('pond:edit.pond.title') : t('pond:create.pond.title')
  const submitLabel = isEditMode ? t('common:save') : t('common:create')

  const handleClose = () => {
    if (isDirty) {
      setConfirmOpen(true)
    } else {
      onClose()
      reset()
    }
  }

  const handleConfirmDiscard = () => {
    setConfirmOpen(false)
    onClose()
    reset()
  }

  return (
    <CustomDialog
      closeDialog={handleClose}
      open={open}
      title={title}
      submitLabel={submitLabel}
      formName="pond-dialog"
      titleAlign="left"
      isSubmitLoading={isLoading}
      disabledSubmit={isLoading || !isDirty}
    >
      <form onSubmit={handleSubmit(onSubmit)} id="pond-dialog">
        <Stack spacing={1}>
          <Typography style={{ fontWeight: 600, marginTop: 20 }}>
            {t('pond:pond.title')}
            <span style={{ color: 'red', marginLeft: 4 }}>*</span>
          </Typography>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                type="text"
                error={!!errors.name}
                inputProps={{ maxLength: 30 }}
                helperText={
                  <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{errors.name ? errors.name.message : ''}</span>
                    <span>{`${field.value?.length ?? 0}/30`}</span>
                  </Box>
                }
              />
            )}
          />
          <Typography style={{ fontWeight: 600, marginTop: 20 }}>
            {t('pond:species.type')}
            <span style={{ color: 'red', marginLeft: 4 }}>*</span>
          </Typography>
          <Controller
            name="species"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                type="text"
                error={!!errors.species}
                helperText={errors.species ? errors.species.message : undefined}
              >
                <MenuItem value={-1} disabled>
                  {t('pond:select.species.type')}
                </MenuItem>
                {loadingSpecies
                  ? 'loading...'
                  : species?.map((el) => (
                    <MenuItem key={el.id} value={el.id}>
                      {el.name}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />
          <Typography style={{ fontWeight: 600, marginTop: 20 }}>
            {t('pond:pond.type')}
            <span style={{ color: 'red', marginLeft: 4 }}>*</span>
          </Typography>
          <Controller
            name="pondType"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                type="text"
                error={!!errors.pondType}
                helperText={errors.pondType ? errors.pondType.message : undefined}
              >
                <MenuItem value={-1} disabled>
                  {t('pond:select.pond.type')}
                </MenuItem>
                {loadingType
                  ? 'loading...'
                  : pondTypes?.map((el) => (
                    <MenuItem key={el.id} value={el.id}>
                      {el.type}
                    </MenuItem>
                  ))}
              </TextField>
            )}
          />

          <Typography style={{ fontWeight: 600, marginTop: 20 }}>
            {t('pond:pond.size')}
            <span style={{ color: 'red', marginLeft: 4 }}>*</span>
          </Typography>
          <Box>
            <Paper
              sx={{
                border: 1,
                borderColor: errors.pondSize ? 'error.main' : 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Controller
                name="pondSize"
                control={control}
                render={({ field }) => (
                  <InputBase
                    {...field}
                    sx={{ ml: 1, flex: 1, p: 1, borderRight: 1, borderColor: 'divider' }}
                    type="number"
                    inputProps={{ 'aria-label': 'pond size' }}
                  />
                )}
              />
              <Controller
                name="areaUnit"
                control={control}
                render={({ field: areaField }) => (
                  <Select
                    {...areaField}
                    variant="standard"
                    disableUnderline
                    sx={{
                      backgroundColor: theme.palette.grey[200],
                      borderRadius: '0 4px 4px 0',
                      '& .MuiSelect-select': {
                        padding: '12px 20px 12px 12px !important',
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    {pondArea?.map((unit) => (
                      <MenuItem key={unit.name} value={unit.name}>
                        {unit.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </Paper>
            {errors.pondSize && (
              <FormHelperText error>
                {errors.pondSize.message}
              </FormHelperText>
            )}
          </Box>
        </Stack>
      </form>
      <UnsavedChangesDialog
        open={confirmOpen}
        onDiscard={handleConfirmDiscard}
        onCancel={() => setConfirmOpen(false)}
      />
    </CustomDialog>
  )
}

export default PondDialog
